import { useEffect, useCallback, useState, useMemo, useRef } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, RefreshCw, RotateCcw, Send, Pencil, ChevronDown, SearchX, AlertTriangle, Trash2, Merge, Lock } from 'lucide-react';
import clsx from 'clsx';
import { Button, ConfirmDialog, useToast } from '@centraoneplus/ui';
import { ticketService } from '@services/ticket.service';
import { csatService } from '@services/csat.service';
import { ReopenTicketModal, type ReopenPayload } from '@components/tickets/ReopenTicketModal';
import { MergeIntoTicketModal } from '@components/tickets/MergeIntoTicketModal';
import { TicketCollisionBanner } from '@components/tickets/TicketCollisionBanner';
import { useTicketCollision } from '@hooks/useTicketCollision';
import type { HistoryEntry as TicketHistoryEntry } from '@services/ticket.service';
import { fileService } from '@services/file.service';
import type { Attachment } from '@components/tickets/FileAttachments';
import { validateAttachmentFiles } from '@components/tickets/attachmentPolicy';
import { filterDisplayableMessages } from '@components/tickets/email-render.utils';
import { useTicketStore } from '@stores/ticketStore';
import { useTicketsListStore } from '@stores/ticketsListStore';
import {
  ConversationThread,
  ReplyEditor,
  TicketSidebar,
  LineItemsTab,
  TicketTabs,
} from '@components/tickets';
import {
  TicketCrumbBar,
  TicketIdentityPlate,
  TicketListNav,
  TicketTitle,
} from '@components/tickets/TicketDetailHeader';
import { ticketTypesService } from '@services/ticket-types.service';
import { HistoryTimeline } from '@components/history';
import { PageHeaderMeta } from '@components/layouts/PageHeaderMeta';
import { ComposerDock } from '@components/tickets/ComposerDock';
import { UndoSendBar } from '@components/tickets/UndoSendBar';
import { useUndoSend } from './useUndoSend';
import { isUndoSendEnabled, newOperationId, undoErrorCode, type ReplySnapshot } from './undoSend';
import type { SendWithUndoResult } from '@services/ticket.service';
import { AISuggestionsPanel } from '@components/tickets/ai/AISuggestionsPanel';
import { ResponseSuggestions } from '@components/tickets/ai';
import { deriveSlaState, deriveOpened, mergedIntoTicketId } from '@components/tickets/ticketFacts';
import { TicketNotesTab } from '@components/tickets/TicketNotesTab';
import type { ReplyEditorHandle, ReplyRecipients } from '@components/tickets/ReplyEditor';
import { recipientsPayload, supportsReplyRecipients } from '@components/tickets/replyRecipients';
import type { HistoryEntry } from '@components/history';
import { useAISuggestions } from '@hooks/useAISuggestions';
import { useScrollToLatest } from '@hooks/useScrollToLatest';
import type { Message, Ticket, TicketStatus, TicketPriority } from '@centraoneplus/shared-types';
import type { PendingReply } from '@components/tickets/ConversationThread';
import { useAuthStore } from '@stores/authStore';
import type { ResponseTone, SuggestionItem } from '../../services/ai.service';
import { HuhokaLeadAction } from '@features/integrations/huhoka/HuhokaLeadAction';
import { lastTicketsView } from '@/lib/lastTicketsView';
import { ParentQueueTriage } from '@components/tickets/ParentQueueTriage';
import { TicketAccessRequestPanel } from '@components/tickets/TicketAccessRequestPanel';
import { TicketAccessRequestsBanner } from '@components/tickets/TicketAccessRequestsBanner';

/** Crumb-bar button recipes. 30px, 2px radius, sentence case, no glyphs. */
const CRUMB_OUTLINE =
  'h-[30px] px-3 rounded-[10px] border border-[var(--co-line-strong)] text-[12.5px] text-[var(--co-ink-2)] transition-colors duration-[120ms] hover:border-[var(--co-brass)] hover:bg-[var(--co-brass-wash)] disabled:opacity-50';
const CRUMB_PRIMARY =
  'h-[30px] px-[14px] rounded-[10px] border border-[var(--co-ink)] bg-[var(--co-ink)] text-[12.5px] font-semibold text-[var(--co-ink-invert)] transition-opacity duration-[120ms] hover:opacity-90 disabled:opacity-50';

/** "in_progress" → "In Progress" for display in the change-confirmation toasts. */
function humanizeLabel(value: string): string {
  return value
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

// Stable content key for an attachment, used to keep a deleted file hidden even
// if it re-surfaces from a different source under a different id. Must match the
// signature format used when merging message + uploaded attachments below.
function attachmentSignature(name: string, size: number): string {
  return `${name} ${size}`;
}

export default function TicketDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  // Set by the Backtrack archive's row click. It lives on this history entry, so
  // it survives a refresh and a round trip through the edit page.
  const fromBacktrack = Boolean((location.state as { fromBacktrack?: boolean } | null)?.fromBacktrack);
  const listTickets = useTicketsListStore((st) => st.tickets);
  // Drops the row from the list page's cached queue so the list we return to
  // after a delete never paints the ticket that no longer exists.
  const removeTicketFromList = useTicketsListStore((st) => st.removeTicketFromList);
  const [replyContent, setReplyContent] = useState('');
  // A note is a different document from a reply — separate draft, separate dock.
  const [noteContent, setNoteContent] = useState('');
  const [replyOpen, setReplyOpen] = useState(false);
  // Status and priority are edited FROM THE BAND now (phase 9 audit); the
  // sidebar's own rows are gone, so there is one control per fact.
  const [noteOpen, setNoteOpen] = useState(false);
  const [showAISuggestions] = useState(true);
  const [isReopening, setIsReopening] = useState(false);
  const [isReopenModalOpen, setIsReopenModalOpen] = useState(false);
  const [isSendingSurvey, setIsSendingSurvey] = useState(false);
  const [showSurveyConfirm, setShowSurveyConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isMergeOpen, setIsMergeOpen] = useState(false);
  const [isMerging, setIsMerging] = useState(false);
  // The "Survey Active" master switch is enforced backend-side, so a manual send
  // against a CSAT-disabled tenant is rejected. Mirror it here so agents aren't
  // offered a button that can only fail. Starts false and only opens up once the
  // config confirms CSAT is on (config read is cached BFF-side).
  const [csatEnabled, setCsatEnabled] = useState(false);
  const toast = useToast();
  // Lets the Notes tab jump the user into writing an internal note in the composer.
  const replyEditorRef = useRef<ReplyEditorHandle>(null);

  // #18947 — live collision detection: who else is on this ticket, and a way to
  // tell them we're typing. notifyTyping(true) is debounced off after a pause.
  const { activeUsers: collisionUsers, notifyTyping } = useTicketCollision(id);
  const typingIdleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleReplyContentChange = useCallback(
    (html: string) => {
      setReplyContent(html);
      notifyTyping(true);
      if (typingIdleTimer.current) clearTimeout(typingIdleTimer.current);
      typingIdleTimer.current = setTimeout(() => notifyTyping(false), 3000);
    },
    [notifyTyping],
  );
  useEffect(() => () => {
    if (typingIdleTimer.current) clearTimeout(typingIdleTimer.current);
  }, []);
  const [activeTab, setActiveTab] = useState<'lineItems' | 'conversation' | 'notes' | 'history'>('lineItems');
  const [typeHasFields, setTypeHasFields] = useState<boolean>(false);
  const [historyEntries, setHistoryEntries] = useState<HistoryEntry[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyHasMore, setHistoryHasMore] = useState(false);

  // Attachments state
  const [fileAttachments, setFileAttachments] = useState<Attachment[]>([]);
  const [isUploadingAttachment, setIsUploadingAttachment] = useState(false);
  // Files attached via the composer and not yet sent. They are uploaded
  // immediately (so they appear in the sidebar) and sent with the next reply
  // (so they appear in the conversation).
  const [pendingAttachments, setPendingAttachments] = useState<Attachment[]>([]);
  // #18937 — deleting a saved attachment is destructive with no undo, so it is
  // confirmed first. This holds the attachment awaiting that confirmation.
  const [attachmentPendingDelete, setAttachmentPendingDelete] = useState<string | null>(null);
  const [isDeletingAttachment, setIsDeletingAttachment] = useState(false);
  // Ids of attachments the user just deleted. A deletable attachment derived
  // from a message isn't in `fileAttachments`, so removing it from that list
  // wouldn't hide it — we filter these out of the combined list instead.
  const [deletedAttachmentIds, setDeletedAttachmentIds] = useState<Set<string>>(new Set());
  // Content signatures ("name size") of deleted attachments. The same file can
  // re-surface after delete under a *different* id (e.g. the message-derived
  // copy carries a synthetic "msg-..." id), so an id-only filter misses it.
  // Matching on name+size catches the re-surfaced copy too. Cleared for a file
  // when it is re-uploaded, so re-uploading a deleted file shows it again.
  const [deletedAttachmentSignatures, setDeletedAttachmentSignatures] = useState<Set<string>>(new Set());

  const {
    ticket,
    messages,
    isLoading,
    isSending,
    isUpdating,
    error,
    notFound,
    accessDenied,
    fetchTicket,
    sendMessage,
    updateStatus,
    updatePriority,
    moveToStage,
    changePipeline,
    assignTicket,
    addSecondaryAssignees,
    removeSecondaryAssignee,
    addTags,
    removeTags,
    updateTicket,
    reset,
  } = useTicketStore();
  const conversation = useTicketStore((s) => s.conversation);
  const sendMessageWithUndo = useTicketStore((s) => s.sendMessageWithUndo);
  const fetchMessages = useTicketStore((s) => s.fetchMessages);
  // To/Cc of an undone reply, handed back to the composer's recipient rows.
  const [restoreRecipients, setRestoreRecipients] = useState<ReplyRecipients | null>(null);
  // Only email replies go out to addresses, so only they get To/Cc fields.
  const isEmailConversation = supportsReplyRecipients(conversation);
  const editMessage = useTicketStore((s) => s.editMessage);
  const deleteMessage = useTicketStore((s) => s.deleteMessage);
  const internalNotes = useMemo(() => messages.filter((m) => m.isInternal), [messages]);
  // Internal notes live in their own Notes tab, so keep them out of the
  // Conversation thread to avoid showing the same content twice.
  const conversationMessages = useMemo(() => messages.filter((m) => !m.isInternal), [messages]);
  // Bug #14955: the tab badge counted conversationMessages directly, but
  // ConversationThread additionally hides the automated ticket-confirmation
  // acknowledgement (see filterDisplayableMessages) - so a ticket whose only
  // message was that acknowledgement showed "Conversation 1" next to the
  // thread's own "No messages in this conversation yet." Filtering the same
  // way here keeps the badge honest about what's actually rendered.
  const displayableConversationMessages = useMemo(
    () => filterDisplayableMessages(conversationMessages),
    [conversationMessages],
  );

  const handleEditNote = useCallback(
    async (messageId: string, content: string) => {
      try {
        await editMessage(messageId, content);
        toast.success('Note updated');
      } catch (err: any) {
        toast.error(
          'Failed to update note',
          err?.response?.data?.message || err?.response?.data?.error?.message || err?.message || 'Please try again.',
        );
        // Rethrow so the notes editor stays open on failure.
        throw err;
      }
    },
    [editMessage, toast],
  );
  const handleDeleteNote = useCallback(
    async (messageId: string) => {
      try {
        await deleteMessage(messageId);
        toast.success('Note deleted');
      } catch (err: any) {
        toast.error(
          'Failed to delete note',
          err?.response?.data?.message || err?.response?.data?.error?.message || err?.message || 'Please try again.',
        );
        // Rethrow so the caller knows the delete failed (note stays in the list).
        throw err;
      }
    },
    [deleteMessage, toast],
  );

  // Auto-scroll the Conversation tab to the newest message on open, plus a
  // floating "Latest" button to jump back after scrolling up into history.
  const {
    scrollContainerRef,
    conversationContentRef,
    onScroll: onConversationScroll,
    showLatest,
    scrollToLatest,
    trackLatest,
    newCount,
  } = useScrollToLatest({
    ticketId: id,
    activeTab,
    isLoading,
    messageCount: conversationMessages.length,
  });

  // Prepare context for AI suggestions
  const ticketContext = useMemo(() => {
    if (!ticket) return null;
    return {
      ticketId: ticket.id,
      subject: ticket.subject,
      description: ticket.description,
      status: ticket.status,
      priority: ticket.priority,
      channel: ticket.channel,
      tags: ticket.tags,
    };
  }, [ticket]);

  const messageContext = useMemo(() => {
    return messages.map((msg) => ({
      content: msg.content || '',
      senderType: msg.senderType as 'customer' | 'agent' | 'system',
      senderName: msg.senderName,
      timestamp: msg.createdAt,
    }));
  }, [messages]);

  const customerContext = useMemo(() => {
    if (!ticket?.customer) return undefined;
    return {
      name: ticket.customer.name,
      email: ticket.customer.email,
    };
  }, [ticket?.customer]);

  // Combine email attachments from messages with uploaded file attachments
  // Uses defensive coding to handle various edge cases
  const allAttachments = useMemo(() => {
    try {
      // Safely extract attachments from messages (email attachments)
      const emailAttachments: Attachment[] = [];

      // A stored email attachment also comes back from the file-service list
      // (`fileAttachments`, keyed by FileUpload.id === the attachment's
      // fileId). To avoid showing it twice, skip the message-derived copy when
      // the same file is already present in that list — the file-service entry
      // is the canonical, properly-downloadable one.
      const linkedFileIds = new Set(fileAttachments.map((f) => f.id));
      // Fallback de-dupe key: not every message attachment carries a `fileId`
      // (older messages, or before the linker has run), so id-matching alone
      // misses those and the file shows up twice. Match on a content signature
      // (name + size) too, so the message-derived copy is suppressed whenever
      // an equivalent file-service entry exists.
      const fileSignature = (name: string, size: number) => `${name} ${size}`;
      const linkedFileSignatures = new Set(
        fileAttachments.map((f) => fileSignature(f.name, f.size)),
      );

      // Keys for every attachment that rides on a message — i.e. an inbound
      // email attachment OR one the agent already sent with a reply. A
      // file-service entry that matches one of these is not a plain sidebar
      // upload, so its delete button must stay hidden.
      const messageAttachmentFileIds = new Set<string>();
      const messageAttachmentSignatures = new Set<string>();

      if (Array.isArray(messages)) {
        for (const msg of messages) {
          // Skip if message or attachments is invalid
          if (!msg || !Array.isArray(msg.attachments) || msg.attachments.length === 0) {
            continue;
          }

          for (let index = 0; index < msg.attachments.length; index++) {
            const att = msg.attachments[index];
            // Skip invalid attachments
            if (!att || typeof att !== 'object') {
              continue;
            }

            // Email attachment bytes are stored in the file-service (fileId).
            // Build the authenticated download-proxy URL from that fileId; the
            // raw `att.url` is a private blob URL that can't be opened directly.
            const fileId = (att as { fileId?: string }).fileId;

            const name = String(att.name || 'Attachment');
            const size = Number(att.size) || 0;

            // Record this attachment as "lives on a message" so the matching
            // file-service entry can be locked from deletion below.
            if (fileId) messageAttachmentFileIds.add(fileId);
            messageAttachmentSignatures.add(fileSignature(name, size));

            const url = fileId
              ? `/api/files/${fileId}/download`
              : String(att.url || '');

            // Anything that rides on a message is locked: an inbound one
            // arrived on the customer's email, and an outbound one has already
            // been sent with a reply. Only plain sidebar uploads (which never
            // appear on a message) are deletable — handled in the merge below.
            const deletable = false;

            // Skip phantom attachments: an entry with no fileId and no URL has
            // nothing to download or preview and shows up as an empty
            // "Attachment" (0 B). Drop it rather than list a dead row.
            if (!fileId && !url) {
              continue;
            }

            // De-dupe: this file is already listed via the file-service panel,
            // either by its fileId or — when the message attachment has no
            // fileId — by a matching name + size signature.
            if (fileId && linkedFileIds.has(fileId)) {
              continue;
            }
            const signature = fileSignature(name, size);
            if (linkedFileSignatures.has(signature)) {
              continue;
            }
            // Remember this one so the same file repeated across messages
            // (e.g. an email and its quoted reply) isn't listed again.
            linkedFileSignatures.add(signature);

            emailAttachments.push({
              // Message-derived attachments aren't deletable, so they keep a
              // synthetic id that handleDeleteAttachment refuses to touch.
              id: `msg-${msg.id || 'unknown'}-${index}`,
              name,
              size,
              mimeType: String(att.mimeType || (att as { contentType?: string }).contentType || 'application/octet-stream'),
              url,
              uploadedAt: String(msg.createdAt || new Date().toISOString()),
              deletable,
            });
          }
        }
      }

      // A plain sidebar upload never appears on a message — those stay deletable.
      // Lock any file-service entry that matches a message attachment (an inbound
      // email file, or one already sent with a reply) by hiding its delete button.
      const uploadedAttachments = fileAttachments.map((f) => ({
        ...f,
        deletable: !(
          messageAttachmentFileIds.has(f.id) ||
          messageAttachmentSignatures.has(fileSignature(f.name, f.size))
        ),
      }));

      // Combine with uploaded file attachments, dropping anything the user just
      // deleted. Filter by id AND by content signature (name+size) so a
      // re-surfaced copy of a deleted file — which may carry a different id
      // (e.g. the message-derived "msg-..." id) — stays hidden too.
      return [...emailAttachments, ...uploadedAttachments].filter(
        (a) =>
          !deletedAttachmentIds.has(a.id) &&
          !deletedAttachmentSignatures.has(attachmentSignature(a.name, a.size)),
      );
    } catch (err) {
      console.error('Error processing attachments:', err);
      return fileAttachments; // Return only uploaded files if email processing fails
    }
  }, [messages, fileAttachments, deletedAttachmentIds, deletedAttachmentSignatures]);

  // AI Suggestions hook
  const {
    suggestions: aiSuggestions,
    isLoading: aiLoading,
    error: aiError,
    selectedTone,
    fetchSuggestions,
  } = useAISuggestions({
    ticket: ticketContext || { ticketId: '', subject: '' },
    messages: messageContext,
    customer: customerContext,
    autoFetch: !!ticketContext && messages.length > 0,
    debounceMs: 1000,
  });

  // Fetch ticket on mount
  useEffect(() => {
    if (id) {
      fetchTicket(id);
    }

    return () => {
      reset();
    };
  }, [id, fetchTicket, reset]);

  // Undo send: put an undone (or failed) reply back into the composer — but
  // never over a new reply the agent has already started; offer it instead.
  const replyContentRef = useRef(replyContent);
  replyContentRef.current = replyContent;
  const pendingAttachmentsRef = useRef(pendingAttachments);
  pendingAttachmentsRef.current = pendingAttachments;
  const activeTabRef = useRef(activeTab);
  activeTabRef.current = activeTab;

  const applySnapshot = useCallback((snapshot: ReplySnapshot) => {
    setReplyContent(snapshot.content);
    setPendingAttachments(snapshot.attachments);
    setRestoreRecipients(snapshot.recipients ?? null);
    setReplyOpen(true);
  }, []);

  const handleRestoreReply = useCallback(
    (snapshot: ReplySnapshot) => {
      const composerBusy =
        replyContentRef.current.replace(/<[^>]*>/g, '').replace(/&nbsp;/gi, ' ').trim().length > 0 ||
        pendingAttachmentsRef.current.length > 0;
      if (!composerBusy) {
        applySnapshot(snapshot);
        return;
      }
      toast.showToast({
        type: 'info',
        title: 'Your undone reply was kept',
        message: 'You have already started another reply. Restore the undone one in its place?',
        duration: 15000,
        action: { label: 'Restore reply', onClick: () => applySnapshot(snapshot) },
      });
    },
    [applySnapshot, toast],
  );

  const handleUndoSendSent = useCallback(async () => {
    await fetchMessages();
    if (activeTabRef.current === 'conversation') trackLatest();
  }, [fetchMessages, trackLatest]);

  const currentUserId = useAuthStore((s) => s.user?.id);
  const undoSend = useUndoSend({
    onSent: handleUndoSendSent,
    onRestore: handleRestoreReply,
    scopeKey: id,
    // Per user + ticket, so reopening the ticket brings back its countdown and Undo.
    persistKey: currentUserId && id ? `${currentUserId}:${id}` : undefined,
  });
  const startUndoSend = undoSend.start;

  // Replies inside their undo window, shown in the thread with their body so
  // the agent sees exactly what is about to go out.
  const agentName = useAuthStore((s) => s.user?.name);
  const pendingReplies = useMemo<PendingReply[]>(
    () =>
      undoSend.pending.map((item) => {
        const seconds = Math.max(0, Math.ceil((item.deadline - undoSend.now) / 1000));
        const to = item.snapshot.recipients?.to?.length
          ? item.snapshot.recipients.to
          : ticket?.contactEmail
            ? [ticket.contactEmail]
            : [];
        return {
          statusLabel:
            item.phase === 'undoing' ? 'Cancelling…' : item.phase === 'sending' || seconds === 0 ? 'Sending…' : `Sending in ${seconds}s`,
          message: {
            id: `pending-${item.operationId}`,
            tenantId: ticket?.tenantId ?? '',
            conversationId: '',
            senderType: 'agent',
            senderName: agentName || 'You',
            content: item.snapshot.content,
            contentType: 'html',
            attachments: item.snapshot.attachments.map((a) => ({
              name: a.name,
              url: a.url,
              mimeType: a.mimeType,
              size: a.size,
              fileId: a.id,
            })),
            metadata: { source: 'email', to, cc: item.snapshot.recipients?.cc ?? [] },
            isInternal: false,
            createdAt: new Date(item.startedAt).toISOString(),
          } as Message,
        };
      }),
    [undoSend.pending, undoSend.now, agentName, ticket?.contactEmail, ticket?.tenantId],
  );

  const handleSendMessage = useCallback(
    async (
      content: string,
      isInternal: boolean,
      mentions?: Array<{ userId: string; userName: string }>,
      recipients?: ReplyRecipients,
    ) => {
      const attachments = pendingAttachments.map((a) => ({
        // Send the file id too: it's the most reliable reference for the backend
        // to fetch the bytes when embedding them in an outbound email. `url` can
        // be empty/relative depending on the storage provider.
        id: a.id,
        fileId: a.id,
        name: a.name,
        url: a.url,
        mimeType: a.mimeType,
        size: a.size,
      }));
      const dto = {
        content,
        contentType: 'html' as const,
        isInternal,
        attachments: attachments.length > 0 ? attachments : undefined,
        // Teammates @mentioned in a note or a reply (in-app notification only).
        mentions: mentions && mentions.length > 0 ? mentions : undefined,
        // Recipients only ever accompany email replies; omitted → contact only.
        ...recipientsPayload(isInternal, recipients),
      };

      // Customer replies get an undo window; internal notes send as before.
      if (!isInternal && isUndoSendEnabled()) {
        const snapshot: ReplySnapshot = { content, recipients, mentions, attachments: pendingAttachments };
        const operationId = newOperationId();
        let result: SendWithUndoResult;
        try {
          result = await sendMessageWithUndo({ ...dto, operationId });
        } catch (error) {
          // The server could not confirm the outcome. Ask once more by id: if the
          // reply was in fact held, track it instead of inviting a duplicate.
          if (undoErrorCode(error) !== 'UNDO_SEND_UNCONFIRMED') throw error;
          const operation = await ticketService.getUndoSendStatus(operationId).catch(() => null);
          if (!operation || operation.status === 'cancelled' || operation.status === 'failed') throw error;
          result = { mode: 'scheduled', operation };
        }
        const receivedAt = Date.now();

        if (result.mode === 'scheduled') {
          startUndoSend(result.operation, snapshot, receivedAt, ticket?.contactEmail || undefined);
        } else {
          if (result.undoUnavailable) {
            toast.info('Reply sent', 'Undo was unavailable, so your reply was sent straight away.');
          }
          if (activeTab === 'conversation') trackLatest();
        }
      } else {
        await sendMessage(dto);
        // Reveal the just-sent message, tracking its async (iframe) render.
        if (activeTab === 'conversation') trackLatest();
      }
      setPendingAttachments([]); // Files have been sent with the message
      setReplyContent(''); // Clear after sending
      setRestoreRecipients(null);
    },
    [sendMessage, sendMessageWithUndo, pendingAttachments, activeTab, trackLatest, startUndoSend, toast, ticket?.contactEmail]
  );

  // "Use" in the header's AI Suggestions: fill the reply and open the composer.
  const handleSelectSuggestion = useCallback((suggestion: SuggestionItem) => {
    setReplyContent(suggestion.content);
    setReplyOpen(true);
  }, []);

  const handleRefreshSuggestions = useCallback(
    (tone?: ResponseTone) => {
      fetchSuggestions(tone);
    },
    [fetchSuggestions]
  );

  // Pop a small confirmation toast after a sidebar change. The store swallows
  // failures into its `error` state instead of throwing, so we read that here to
  // decide between a success and an error toast.
  const notifySidebarChange = useCallback(
    (successTitle: string, successMessage?: string) => {
      const err = useTicketStore.getState().error;
      if (err) {
        toast.error('Update failed', err);
      } else {
        toast.success(successTitle, successMessage);
      }
    },
    [toast]
  );

  const handleUpdateStatus = useCallback(
    async (status: TicketStatus) => {
      await updateStatus(status);
      notifySidebarChange('Status updated', `Changed to ${humanizeLabel(status)}`);
    },
    [updateStatus, notifySidebarChange]
  );

  const handleUpdatePriority = useCallback(
    async (priority: TicketPriority) => {
      await updatePriority(priority);
      notifySidebarChange('Priority updated', `Set to ${humanizeLabel(priority)}`);
    },
    [updatePriority, notifySidebarChange]
  );

  const handleUpdateStage = useCallback(
    async (stageId: string) => {
      await moveToStage(stageId);
      notifySidebarChange('Stage updated');
    },
    [moveToStage, notifySidebarChange]
  );

  const handleUpdatePipeline = useCallback(
    async (pipelineId: string) => {
      await changePipeline(pipelineId);
      notifySidebarChange('Pipeline updated');
    },
    [changePipeline, notifySidebarChange]
  );

  const handleAssign = useCallback(
    async (assigneeId: string | null) => {
      await assignTicket(assigneeId);
      notifySidebarChange(
        assigneeId ? 'Ticket assigned' : 'Assignee removed',
        assigneeId ? undefined : 'This ticket is now unassigned'
      );
    },
    [assignTicket, notifySidebarChange]
  );

  const handleAddSecondaryAssignees = useCallback(
    async (userIds: string[]) => {
      await addSecondaryAssignees(userIds);
      notifySidebarChange('Secondary agent added');
    },
    [addSecondaryAssignees, notifySidebarChange]
  );

  const handleRemoveSecondaryAssignee = useCallback(
    async (userId: string) => {
      await removeSecondaryAssignee(userId);
      notifySidebarChange('Secondary agent removed');
    },
    [removeSecondaryAssignee, notifySidebarChange]
  );

  const handleAddTags = useCallback(
    async (tags: string[]) => {
      await addTags(tags);
      notifySidebarChange(
        tags.length > 1 ? 'Tags added' : 'Tag added',
        tags.join(', ')
      );
    },
    [addTags, notifySidebarChange]
  );

  const handleRemoveTags = useCallback(
    async (tags: string[]) => {
      await removeTags(tags);
      notifySidebarChange(
        tags.length > 1 ? 'Tags removed' : 'Tag removed',
        tags.join(', ')
      );
    },
    [removeTags, notifySidebarChange]
  );

  const handleUpdateTicketType = useCallback(
    async (ticketTypeId: string | undefined) => {
      await updateTicket({ ticketTypeId: ticketTypeId ?? null });
      notifySidebarChange('Ticket type updated');
    },
    [updateTicket, notifySidebarChange]
  );

  // The internal flag is set by the CALL SITE now, never by editor state —
  // that is what the reply/note split buys. Each wrapper also collapses its own
  // dock on success.
  const handleSendReply = useCallback(
    async (
      content: string,
      _isInternal: boolean,
      mentions?: Array<{ userId: string; userName: string }>,
      recipients?: ReplyRecipients,
    ) => {
      await handleSendMessage(content, false, mentions, recipients);
      setReplyOpen(false);
    },
    [handleSendMessage],
  );

  const handleSendNote = useCallback(
    async (
      content: string,
      _isInternal: boolean,
      mentions?: Array<{ userId: string; userName: string }>,
    ) => {
      await handleSendMessage(content, true, mentions);
      setNoteOpen(false);
    },
    [handleSendMessage],
  );

  const handleRefresh = useCallback(() => {
    if (id) {
      fetchTicket(id);
    }
  }, [id, fetchTicket]);

  const handleBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  // The crumb says "Tickets", so it goes to Tickets — not to wherever the
  // browser was before (a customer profile, the inbox, another tab's URL).
  // Opened from the Backtrack archive, the crumb says "Backtrack" and goes there.
  const handleBackToTickets = useCallback(() => {
    navigate(fromBacktrack ? '/backtrack' : lastTicketsView());
  }, [navigate, fromBacktrack]);

  // Delete is terminal: the ticket is gone, so there is nothing to return to on
  // this route. Send the user back where they came from — the list page if its
  // queue holds this ticket (the same signal listNav uses), otherwise the board
  // — with `replace` so Back can't land on the dead detail route.
  const handleDeleteTicket = useCallback(async () => {
    if (!id) return;
    const cameFromList = (listTickets ?? []).some((t) => t.id === id);
    setIsDeleting(true);
    try {
      await ticketService.deleteTicket(id);
      removeTicketFromList(id);
      setShowDeleteConfirm(false);
      toast.success('Ticket deleted', `#${ticket?.ticketNumber || id} has been deleted.`);
      navigate(cameFromList ? '/tickets/list' : '/tickets', { replace: true });
    } catch (err: any) {
      toast.error(
        'Delete failed',
        err?.response?.data?.message ||
          err?.response?.data?.error?.message ||
          err?.message ||
          'Could not delete this ticket. Please try again.',
      );
    } finally {
      setIsDeleting(false);
    }
  }, [id, listTickets, removeTicketFromList, navigate, toast, ticket?.ticketNumber]);

  const fetchHistory = useCallback(async (page = 1, append = false) => {
    if (!id) return;
    setHistoryLoading(true);
    try {
      const result = await ticketService.getHistory(id, { page, limit: 50 });
      const mapped: HistoryEntry[] = result.data.map((e: TicketHistoryEntry) => ({
        ...e,
        description: e.description || undefined,
        actorId: e.actorId || undefined,
        actorName: e.actorName || undefined,
        ticketId: e.ticketId || undefined,
        customerId: e.customerId || undefined,
      }));
      setHistoryEntries(prev => append ? [...prev, ...mapped] : mapped);
      setHistoryPage(page);
      setHistoryHasMore(page < result.totalPages);
    } catch (err: any) {
      console.error('Failed to load ticket history:', err);
      toast.error(
        'Failed to load history',
        err?.response?.data?.message || err?.response?.data?.error?.message || err?.message || 'Could not load ticket history. Please try again.',
      );
    } finally {
      setHistoryLoading(false);
    }
  }, [id, toast]);

  const handleLoadMoreHistory = useCallback(() => {
    fetchHistory(historyPage + 1, true);
  }, [fetchHistory, historyPage]);

  // Fetch file attachments for the ticket (uploaded files).
  // Safety net: if the first fetch returns zero files, ask the server to
  // reconcile any email attachments that might still be tagged under the
  // inbound-email entity (event-processing race / stale linker) and re-fetch.
  // Idempotent and cheap — the reconcile returns quickly when there's
  // nothing to do.
  const fetchFileAttachments = useCallback(async () => {
    if (!id) return;
    const mapFiles = (files: any[]): Attachment[] =>
      files.map((file) => ({
        id: file.id,
        name: file.originalFilename || file.filename || 'File',
        size: file.size || 0,
        mimeType: file.mimeType || 'application/octet-stream',
        url: file.url || '',
        uploadedBy: file.uploadedBy,
        uploadedAt: file.createdAt || new Date().toISOString(),
      }));
    try {
      let files = await fileService.getFilesByEntity('TICKET', id);
      if (Array.isArray(files) && files.length === 0) {
        try {
          const res = await ticketService.reconcileAttachments(id);
          if (res?.linked && res.linked > 0) {
            files = await fileService.getFilesByEntity('TICKET', id);
          }
        } catch (reconcileErr) {
          // Reconcile is best-effort; don't break the page if it fails.
          console.warn('Attachment reconcile failed:', reconcileErr);
        }
      }
      if (Array.isArray(files)) {
        setFileAttachments(mapFiles(files));
      }
    } catch (err) {
      // Silently fail - attachments are not critical
      console.error('Failed to fetch attachments:', err);
      setFileAttachments([]);
    }
  }, [id]);

  // Merge other tickets INTO this one (this ticket is kept). One request per
  // source, sequentially, so a mid-way failure can report exactly what merged.
  // Merged sources are dropped from the cached list (they are closed now) and
  // this ticket is re-fetched to show the conversations that moved onto it.
  const handleMergeIntoThis = useCallback(
    async (sources: Ticket[], note: string) => {
      if (!id || sources.length === 0) return;
      setIsMerging(true);
      const merged: Ticket[] = [];
      try {
        for (const source of sources) {
          await ticketService.mergeTickets(id, [source.id], note || undefined);
          merged.push(source);
          removeTicketFromList(source.id);
        }
        setIsMergeOpen(false);
        toast.success(
          'Tickets merged',
          `${merged.map((t) => `#${t.ticketNumber}`).join(', ')} merged into #${ticket?.ticketNumber || id} and closed.`,
        );
      } catch (err: any) {
        const reason =
          err?.response?.data?.message ||
          err?.response?.data?.error?.message ||
          err?.message ||
          'Please try again.';
        toast.error(
          'Merge failed',
          merged.length > 0
            ? `${merged.map((t) => `#${t.ticketNumber}`).join(', ')} merged; the rest did not. ${reason}`
            : reason,
        );
        if (merged.length > 0) setIsMergeOpen(false);
      } finally {
        setIsMerging(false);
        if (merged.length > 0) {
          fetchTicket(id);
          // Their ticket attachments moved here too; attachments otherwise load only on id change.
          fetchFileAttachments();
        }
      }
    },
    [id, removeTicketFromList, fetchTicket, fetchFileAttachments, toast, ticket?.ticketNumber],
  );

  // Upload attachments (used by both the sidebar drop-zone and the composer
  // paperclip). Files are uploaded immediately so they show up in the sidebar.
  // The `stage` flag also queues them as pending attachments for the next reply
  // so they get sent into the conversation.
  const handleUploadAttachments = useCallback(async (files: File[], stage = false) => {
    if (!id) return;

    // Enforce the attachment whitelist for every upload path (sidebar drop zone
    // AND the reply composer paperclip funnel through here). The browser's accept
    // attribute only filters the picker dialog, so disallowed files (e.g. .exe)
    // can still arrive — reject them before uploading.
    const { valid, errors } = validateAttachmentFiles(files);
    if (errors.length > 0) {
      toast.error('Some files were not allowed', errors.join('. '));
    }
    if (valid.length === 0) return;

    setIsUploadingAttachment(true);
    try {
      // Upload each file independently so one failure (e.g. over the server
      // limit) doesn't discard the others — we report per-file outcomes below.
      const { succeeded: uploaded, failed } = await fileService.uploadFilesSettled(valid, {
        entityType: 'TICKET',
        entityId: id,
      });
      // A re-uploaded file must show again: clear its id/signature from the
      // deleted-attachment guards (and their persisted copy) so it is no longer
      // filtered out.
      if (Array.isArray(uploaded) && uploaded.length > 0) {
        const clearedSigs = new Set(
          uploaded.map((f) =>
            attachmentSignature(f.originalFilename || f.filename || 'File', f.size || 0),
          ),
        );
        const clearedIds = new Set(uploaded.map((f) => f.id));
        const nextIds = new Set([...deletedAttachmentIds].filter((x) => !clearedIds.has(x)));
        const nextSigs = new Set(
          [...deletedAttachmentSignatures].filter((s) => !clearedSigs.has(s)),
        );
        setDeletedAttachmentIds(nextIds);
        setDeletedAttachmentSignatures(nextSigs);
        try {
          localStorage.setItem(
            `deletedAttachments:${id}`,
            JSON.stringify({ ids: [...nextIds], sigs: [...nextSigs] }),
          );
        } catch {
          // Ignore storage errors — the in-memory clear still applies.
        }
      }
      if (stage && Array.isArray(uploaded)) {
        const staged: Attachment[] = uploaded.map((file) => ({
          id: file.id,
          name: file.originalFilename || file.filename || 'File',
          size: file.size || 0,
          mimeType: file.mimeType || 'application/octet-stream',
          url: file.url || '',
          uploadedBy: file.uploadedBy,
          uploadedAt: file.createdAt || new Date().toISOString(),
        }));
        setPendingAttachments((prev) => [...prev, ...staged]);
      }
      // Refresh attachments list after upload
      await fetchFileAttachments();

      // Annotate the history timeline for sidebar uploads. Composer (staged)
      // attachments are already captured by the message-sent history once the
      // reply is sent, so we skip those to avoid double entries.
      if (!stage && Array.isArray(uploaded) && uploaded.length > 0) {
        await ticketService.recordAttachmentActivity(
          id,
          'added',
          uploaded.map((f) => ({ name: f.originalFilename || f.filename, fileId: f.id })),
        );
      }

      // Report per-file outcomes. A partial failure shows a summary toast plus a
      // second toast listing each failed file and its reason; a total failure
      // shows just the reasons. Full success stays quiet — the files appear.
      if (failed.length > 0) {
        const detail = failed.map((f) => `${f.file.name} — ${f.error}`).join('; ');
        if (uploaded.length > 0) {
          toast.success(
            'Attachments uploaded',
            `${uploaded.length} uploaded, ${failed.length} failed`,
          );
          toast.error(
            `${failed.length} file${failed.length > 1 ? 's' : ''} failed to upload`,
            detail,
          );
        } else {
          toast.error('Upload failed', detail);
        }
      }
    } catch (err: any) {
      console.error('Failed to upload attachments:', err);
      toast.error(
        'Upload failed',
        err?.response?.data?.message || err?.message || 'Could not upload the file. Please try again.',
      );
    } finally {
      setIsUploadingAttachment(false);
    }
  }, [id, fetchFileAttachments, deletedAttachmentIds, deletedAttachmentSignatures, toast]);

  // Attach from the reply composer: upload + stage for the next message.
  const handleAttachFromComposer = useCallback(
    (files: File[]) => handleUploadAttachments(files, true),
    [handleUploadAttachments],
  );

  // Remove a staged (not-yet-sent) attachment: delete the uploaded file and
  // drop it from both the pending list and the sidebar.
  const handleRemovePendingAttachment = useCallback(async (attachmentId: string) => {
    setPendingAttachments((prev) => prev.filter((a) => a.id !== attachmentId));
    try {
      await fileService.deleteFile(attachmentId);
      setFileAttachments((prev) => prev.filter((a) => a.id !== attachmentId));
    } catch (err: any) {
      console.error('Failed to remove attachment:', err);
      toast.error(
        'Failed to remove attachment',
        err?.response?.data?.message || err?.response?.data?.error?.message || err?.message || 'Could not remove this attachment. Please try again.',
      );
    }
  }, [toast]);

  // Delete an attachment. Inbound email attachments carry a synthetic
  // "msg-{messageId}-{index}" id and aren't ours to delete — the delete button
  // is hidden for them, and this guard is a final safety net.
  const handleDeleteAttachment = useCallback(async (attachmentId: string) => {
    if (attachmentId.startsWith('msg-')) {
      console.warn('Cannot delete email attachments');
      return;
    }
    const removed = fileAttachments.find((a) => a.id === attachmentId);
    try {
      await fileService.deleteFile(attachmentId);
      // Remove from the uploaded-files list and record the id + content
      // signature so it stays filtered out of the combined list — even after a
      // hard refresh, and even if it re-surfaces under a different id.
      setFileAttachments((prev) => prev.filter((a) => a.id !== attachmentId));
      const nextIds = new Set(deletedAttachmentIds).add(attachmentId);
      const nextSigs = new Set(deletedAttachmentSignatures);
      if (removed) nextSigs.add(attachmentSignature(removed.name, removed.size));
      setDeletedAttachmentIds(nextIds);
      setDeletedAttachmentSignatures(nextSigs);
      // Persist so the deletion survives a hard refresh (see hydrate effect).
      if (id) {
        try {
          localStorage.setItem(
            `deletedAttachments:${id}`,
            JSON.stringify({ ids: [...nextIds], sigs: [...nextSigs] }),
          );
        } catch {
          // Ignore storage quota / privacy-mode errors — worst case the
          // in-memory guard still hides it for this session.
        }
      }
      toast.success('Attachment deleted');
      if (id) {
        await ticketService.recordAttachmentActivity(id, 'removed', [
          { name: removed?.name, fileId: attachmentId },
        ]);
      }
    } catch (err) {
      console.error('Failed to delete attachment:', err);
      toast.error('Delete failed', 'Could not delete this attachment. Please try again.');
    }
  }, [id, fileAttachments, deletedAttachmentIds, deletedAttachmentSignatures, toast]);

  // #18937 — open a confirmation instead of deleting on the first click.
  const requestDeleteAttachment = useCallback(async (attachmentId: string) => {
    // Email attachments can't be deleted (handleDeleteAttachment refuses them too).
    if (attachmentId.startsWith('msg-')) return;
    setAttachmentPendingDelete(attachmentId);
  }, []);

  const confirmDeleteAttachment = useCallback(async () => {
    if (!attachmentPendingDelete) return;
    setIsDeletingAttachment(true);
    try {
      await handleDeleteAttachment(attachmentPendingDelete);
    } finally {
      setIsDeletingAttachment(false);
      setAttachmentPendingDelete(null);
    }
  }, [attachmentPendingDelete, handleDeleteAttachment]);

  const attachmentPendingName = attachmentPendingDelete
    ? fileAttachments.find((a) => a.id === attachmentPendingDelete)?.name
    : undefined;

  // Load ticket history whenever the History tab is open, and refresh it after
  // any action — a ticket change (status/priority/assignment/stage/tags bumps
  // updatedAt) or a new message/note (messages length changes) — so the timeline
  // stays current without needing a manual Refresh click.
  useEffect(() => {
    if (activeTab === 'history') {
      fetchHistory(1);
    }
  }, [activeTab, fetchHistory, ticket?.updatedAt, messages.length]);

  // Load file attachments when ticket loads
  useEffect(() => {
    if (id) {
      fetchFileAttachments();
    }
  }, [id, fetchFileAttachments]);

  // Hydrate the deleted-attachment guards for this ticket from localStorage so a
  // deletion sticks across a hard refresh. The in-memory guards above are reset
  // on reload, which let a just-deleted file re-surface from the list source;
  // persisting both its id and content signature keeps it filtered out. Keyed
  // per ticket.
  useEffect(() => {
    if (!id) return;
    try {
      const raw = localStorage.getItem(`deletedAttachments:${id}`);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      // Back-compat: an older build stored a bare array of ids.
      const ids: string[] = Array.isArray(parsed) ? parsed : parsed.ids || [];
      const sigs: string[] = Array.isArray(parsed) ? [] : parsed.sigs || [];
      setDeletedAttachmentIds(new Set(ids));
      setDeletedAttachmentSignatures(new Set(sigs));
    } catch {
      // Corrupt/unavailable storage — fall back to the empty in-memory sets.
    }
  }, [id]);

  // Show the Line Items tab only when the ticket type defines custom fields.
  // Picks the default active tab once the ticket (and its type schema) loads:
  // Line Items when the type has fields, otherwise Conversation. Re-runs when
  // the ticket or its type changes (e.g. via the sidebar). Waits for the ticket
  // to load first — otherwise it would default to Conversation while the type
  // is still unknown and never switch back to Line Items.
  const ticketTypeIdForFields =
    (ticket as any)?.ticketType?.id ?? (ticket as any)?.ticketTypeId;
  useEffect(() => {
    if (!ticket) return;
    let cancelled = false;
    if (!ticketTypeIdForFields) {
      setTypeHasFields(false);
      setActiveTab('conversation');
      return;
    }
    (async () => {
      try {
        const schema = await ticketTypesService.getFormSchema(ticketTypeIdForFields);
        if (cancelled) return;
        const has = (schema.fieldGroups ?? []).some(
          (g: any) => (g.fields ?? []).length > 0,
        );
        setTypeHasFields(has);
        setActiveTab(has ? 'lineItems' : 'conversation');
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to load ticket type schema:', err);
        setTypeHasFields(false);
        setActiveTab('conversation');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ticket?.id, ticketTypeIdForFields]);

  // Resolve whether CSAT is switched on for this tenant, to decide if the
  // "Send Survey" action is offered at all. Fail-closed: a failed read leaves
  // the button hidden rather than showing one that the backend will reject.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const csatConfig = await csatService.getConfig();
        if (!cancelled) setCsatEnabled(!!csatConfig.enabled);
      } catch {
        if (!cancelled) setCsatEnabled(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Opens the reopen modal; the actual API call happens from its submit handler.
  const handleSendSurvey = useCallback(async () => {
    if (!id || !ticket) return;
    setIsSendingSurvey(true);
    try {
      const result = await csatService.sendManualSurvey({
        ticketId: id,
        customerId: ticket.customerId || undefined,
      });
      if (result.success) {
        toast.success(
          'Survey sent',
          ticket.contactEmail ? `Sent to ${ticket.contactEmail}` : result.message,
        );
      } else {
        toast.error('Could not send survey', result.message);
      }
    } catch (err: any) {
      console.error('Failed to send manual survey:', err);
      toast.error(
        'Survey send failed',
        err?.response?.data?.message || err?.message || 'Please try again.',
      );
    } finally {
      setIsSendingSurvey(false);
      setShowSurveyConfirm(false);
    }
  }, [id, ticket, toast]);

  const handleReopenTicket = useCallback(() => {
    if (!id) return;
    setIsReopenModalOpen(true);
  }, [id]);

  const handleReopenSubmit = useCallback(
    async (payload: ReopenPayload) => {
      if (!id) return;
      setIsReopening(true);
      try {
        await ticketService.reopenTicket(id, payload);
        fetchTicket(id);
        setIsReopenModalOpen(false);
        toast.success(
          'Ticket reopened',
          ticket?.ticketNumber ? `#${ticket.ticketNumber} is back on the board` : 'Ticket is back on the board',
        );
      } catch (err: any) {
        console.error('Failed to reopen ticket:', err);
        toast.error(
          'Reopen failed',
          err?.response?.data?.message || err?.message || 'Could not reopen this ticket. Please try again.',
        );
      } finally {
        setIsReopening(false);
      }
    },
    [id, fetchTicket, toast, ticket?.ticketNumber],
  );

  // Plan 6 §Decision 2: the composer belongs to the tabs you can write into.
  // Line Items and History use the full column height instead.
  // Dock-derived values. `hasDraft` reads the editor's own HTML, so an empty
  // paragraph left behind by contentEditable doesn't count as a draft.
  const htmlHasText = (html: string) =>
    html.replace(/<[^>]*>/g, '').replace(/&nbsp;/gi, ' ').trim().length > 0;
  const hasReplyDraft = htmlHasText(replyContent);
  const hasNoteDraft = htmlHasText(noteContent);
  const requesterFirstName = ticket?.contactName?.trim().split(/\s+/)[0];
  // Only count real, loaded suggestions — a zero or a guess in the collapsed
  // hint would be a lie about what's waiting inside.
  const suggestionCount = aiSuggestions?.suggestions?.length ?? 0;

  // ONE derivation, shared by the band's countdown and the sidebar's two bars.
  // Two percentage calculations that can disagree is the bug this prevents.
  const sla = deriveSlaState(ticket);

  // Prev/next walk the queue the list page already holds. Absent — not disabled
  // — on a deep link, where the store has no list to walk.
  const listNav = useMemo(() => {
    const ids = (listTickets ?? []).map((t) => t.id);
    const i = id ? ids.indexOf(id) : -1;
    if (i === -1 || ids.length < 2) return null;
    return {
      index: i + 1,
      total: ids.length,
      onPrev: i > 0 ? () => navigate('/tickets/' + ids[i - 1]) : undefined,
      onNext: i < ids.length - 1 ? () => navigate('/tickets/' + ids[i + 1]) : undefined,
    };
  }, [listTickets, id, navigate]);

  // A merged ticket is closed and its conversations now live on the ticket it
  // was merged into (metadata.mergedInto). It is read-only here: replying,
  // editing, resolving or reopening it would fork the conversation again.
  const isMerged = (ticket?.status as string) === 'merged';
  const mergedIntoId = mergedIntoTicketId(ticket);
  const { data: mergedIntoTicket } = useQuery({
    queryKey: ['ticket-merged-into', mergedIntoId],
    queryFn: () => ticketService.getTicket(mergedIntoId!),
    enabled: !!mergedIntoId,
    staleTime: 60_000,
  });

  const showComposer = activeTab === 'conversation' || activeTab === 'notes';

  // Full-page spinner only for a first load or a different ticket — a manual
  // refresh of the ticket on screen keeps it visible (the button spins).
  if (isLoading && (!ticket || ticket.id !== id)) {
    return (
      <div className="h-full flex items-center justify-center bg-[var(--co-surface)]">
        <RefreshCw className="h-8 w-8 text-gray-400 dark:text-gray-500 animate-spin" />
      </div>
    );
  }

  // Only replace the whole page when there is NO ticket to show (a failed load
  // or a 404). A transient action error — e.g. a stage move the server refuses
  // because the target stage is at its WIP limit (#20581) — keeps the ticket
  // loaded, so it must stay on screen; the store surfaces that error as a toast
  // via notifySidebarChange instead of blanking the page.
  if (!ticket) {
    const isNotFound = notFound;
    // Both are final answers about this link: offer a way out, not a retry.
    const isDeadEnd = isNotFound || accessDenied;
    return (
      <div className="h-full flex flex-col items-center justify-center bg-[var(--co-surface)] px-4">
        <div className="max-w-md w-full text-center">
          <div
            className={clsx(
              'w-14 h-14 mx-auto mb-6 rounded-[2px] border flex items-center justify-center',
              isDeadEnd
                ? 'border-[var(--co-line-strong)] text-[var(--co-ink-4)]'
                : 'border-[var(--co-burgundy)] bg-[var(--co-burgundy-wash)] text-[var(--co-burgundy)]',
            )}
          >
            {accessDenied ? (
              <Lock className="w-7 h-7" />
            ) : isNotFound ? (
              <SearchX className="w-7 h-7" />
            ) : (
              <AlertTriangle className="w-7 h-7" />
            )}
          </div>
          <h1 className="font-display text-[30px] font-extrabold tracking-[-0.03em] leading-[1.1] text-[var(--co-ink)] mb-3">
            {accessDenied ? 'No access to this ticket' : isNotFound ? 'Ticket not found' : 'Something went wrong'}
          </h1>
          <p className="text-[14px] leading-[1.6] text-[var(--co-ink-3)] mb-7 [text-wrap:pretty]">
            {accessDenied
              ? 'This ticket is assigned outside your queue, so you can’t open it. Ask its agent or your supervisor if you need it.'
              : isNotFound
                ? 'This ticket no longer exists. It may have been deleted, or the link you followed is out of date.'
                : error || 'We couldn’t load this ticket. Please try again.'}
          </p>
          {/* The head of the ticket's team decides its requests even when they can't
              open it. Renders nothing for anyone else. */}
          {accessDenied && id && (
            <div className="mb-7 text-left">
              <TicketAccessRequestsBanner
                ticketId={id}
                highlightRequestId={searchParams.get('accessRequest')}
                onDecided={handleRefresh}
              />
            </div>
          )}
          {accessDenied && id && <TicketAccessRequestPanel ticketId={id} onAccessGranted={handleRefresh} />}
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button variant="outline" onClick={handleBack}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Go Back
            </Button>
            {isDeadEnd ? (
              <Button onClick={() => navigate('/tickets')}>
                Go to Tickets
              </Button>
            ) : (
              <Button onClick={handleRefresh}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Try Again
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-[var(--co-canvas)]">
      {/* The crumb bar is the ONLY full-width band. Everything below it is a
          column, which is what lets the record column run the full height
          beside the conversation instead of starting halfway down the page. */}
      <TicketCrumbBar
        onBack={handleBackToTickets}
        backLabel={fromBacktrack ? 'Backtrack' : undefined}
        title={
          <TicketTitle
            ticketNumber={ticket.ticketNumber}
            channel={ticket.channel}
            typeLabel={(ticket as any).ticketType?.name}
            subject={ticket.subject}
          />
        }
        listNav={
          listNav ? (
            <TicketListNav
              index={listNav.index}
              total={listNav.total}
              onPrev={listNav.onPrev}
              onNext={listNav.onNext}
            />
          ) : undefined
        }
        meta={
          /* Utility cluster — the crumb bar is this route's topmost header. */
          <PageHeaderMeta />
        }
        actions={
          <>
            {/* Sentence case, 30px, no glyphs. One dark action: the thing you
                came here to do. Refresh re-fetches the ticket on demand. */}
            <button
              type="button"
              onClick={handleRefresh}
              disabled={isLoading}
              title="Refresh ticket"
              className={clsx(CRUMB_OUTLINE, 'inline-flex items-center gap-1.5')}
            >
              <RefreshCw className={clsx('h-3.5 w-3.5', isLoading && 'animate-spin')} />
              Refresh
            </button>
            {!isMerged && (
              <button
                type="button"
                onClick={() => navigate(`/tickets/${id}/edit`)}
                className={CRUMB_OUTLINE}
              >
                Edit
              </button>
            )}
            {!isMerged && (
              <button
                type="button"
                onClick={() => setIsMergeOpen(true)}
                disabled={isMerging}
                title="Merge other tickets into this one"
                className={clsx(CRUMB_OUTLINE, 'inline-flex items-center gap-1.5')}
              >
                <Merge className="h-3.5 w-3.5" />
                {isMerging ? 'Merging…' : 'Merge'}
              </button>
            )}
            {/* Destructive, so it reads red on hover — outline like its
                neighbours, never the cluster's one dark action. */}
            <button
              type="button"
              onClick={() => setShowDeleteConfirm(true)}
              disabled={isDeleting}
              title="Delete ticket"
              className={clsx(
                CRUMB_OUTLINE,
                'inline-flex items-center gap-1.5 hover:border-red-500 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950/40',
              )}
            >
              <Trash2 className="h-3.5 w-3.5" />
              {isDeleting ? 'Deleting…' : 'Delete'}
            </button>
            {/* HuHoKa CRM: Create lead (link ACTIVE) or the lead badge. */}
            <HuhokaLeadAction ticketId={id} buttonClassName={CRUMB_OUTLINE} />
            {!isMerged && csatEnabled && ticket.contactEmail && (
              <button
                type="button"
                onClick={() => setShowSurveyConfirm(true)}
                disabled={isSendingSurvey}
                title={`Send a CSAT survey to ${ticket.contactEmail}`}
                className={clsx(CRUMB_OUTLINE, 'inline-flex items-center gap-1.5')}
              >
                <Send className="h-3.5 w-3.5" />
                {isSendingSurvey ? 'Sending…' : 'Send survey'}
              </button>
            )}
            {isMerged ? null : (ticket.status === 'resolved' || ticket.status === 'closed') ? (
              <button
                type="button"
                onClick={handleReopenTicket}
                disabled={isReopening}
                className={CRUMB_PRIMARY}
              >
                {isReopening ? 'Reopening…' : 'Reopen'}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => handleUpdateStatus('resolved' as TicketStatus)}
                disabled={isUpdating}
                className={CRUMB_PRIMARY}
              >
                Resolve ticket
              </button>
            )}
          </>
        }
      />
      {isMerged && (
        <div
          role="status"
          className="mx-4 mt-3 flex flex-wrap items-center justify-between gap-3 rounded-[10px] border border-[var(--co-line-strong)] bg-[var(--co-raised)] px-4 py-3"
        >
          <p className="flex items-center gap-2 text-[13px] text-[var(--co-ink-2)]">
            <Merge className="h-4 w-4 flex-none text-[var(--co-ink-3)]" />
            {mergedIntoTicket?.ticketNumber
              ? `This ticket was merged into #${mergedIntoTicket.ticketNumber}. Its conversation continues there.`
              : 'This ticket was merged into another ticket. Its conversation continues there.'}
          </p>
          {mergedIntoId && (
            <button
              type="button"
              onClick={() => navigate(`/tickets/${mergedIntoId}`)}
              className={CRUMB_OUTLINE}
            >
              {mergedIntoTicket?.ticketNumber ? `Open #${mergedIntoTicket.ticketNumber}` : 'Open merged ticket'}
            </button>
          )}
        </div>
      )}
      {/* Team hierarchy: a ticket waiting in a parent team's queue. */}
      {id && (
        <ParentQueueTriage ticketId={id} teamId={ticket.teamId} onMoved={() => fetchTicket(id)} />
      )}
      {/* Access requests from mentioned agents who cannot see this ticket. */}
      {id && (
        <TicketAccessRequestsBanner
          ticketId={id}
          highlightRequestId={searchParams.get('accessRequest')}
          onDecided={() => fetchTicket(id)}
        />
      )}
      {/* Send Survey Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showSurveyConfirm}
        onClose={() => {
          if (!isSendingSurvey) setShowSurveyConfirm(false);
        }}
        onConfirm={handleSendSurvey}
        title="Send CSAT Survey"
        message={
          ticket?.contactEmail
            ? `Send a CSAT survey to ${ticket.contactEmail} for ticket #${ticket?.ticketNumber || id}?`
            : 'Send a CSAT survey for this ticket?'
        }
        confirmLabel="Send Survey"
        cancelLabel="Cancel"
        loading={isSendingSurvey}
      />

      {/* #18937 — confirm before deleting a saved attachment (no undo). */}
      <ConfirmDialog
        isOpen={!!attachmentPendingDelete}
        onClose={() => {
          if (!isDeletingAttachment) setAttachmentPendingDelete(null);
        }}
        onConfirm={confirmDeleteAttachment}
        title="Delete attachment"
        message={
          attachmentPendingName
            ? `Delete "${attachmentPendingName}"? This can't be undone.`
            : "Delete this attachment? This can't be undone."
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
        loading={isDeletingAttachment}
      />

      {/* Delete Ticket Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => {
          if (!isDeleting) setShowDeleteConfirm(false);
        }}
        onConfirm={handleDeleteTicket}
        title="Delete ticket"
        message={`Delete ticket #${ticket?.ticketNumber || id}? This permanently removes the ticket and its conversation. This cannot be undone.`}
        confirmLabel="Delete ticket"
        cancelLabel="Cancel"
        variant="danger"
        loading={isDeleting}
      />

      {/* Merge other tickets into this one */}
      <MergeIntoTicketModal
        isOpen={isMergeOpen}
        onClose={() => {
          if (!isMerging) setIsMergeOpen(false);
        }}
        currentTicket={{ id: ticket.id, ticketNumber: ticket.ticketNumber, subject: ticket.subject }}
        onMerge={handleMergeIntoThis}
        isSubmitting={isMerging}
      />

      {/* Reopen Modal */}
      <ReopenTicketModal
        isOpen={isReopenModalOpen}
        onClose={() => {
          if (!isReopening) setIsReopenModalOpen(false);
        }}
        onSubmit={handleReopenSubmit}
        ticketNumber={ticket?.ticketNumber}
        ticketSubject={ticket?.subject}
        pipelineId={ticket?.pipelineId ?? null}
        isSubmitting={isReopening}
      />

      {/* Main content — floating cards on the canvas, 14px apart, aligned
          with the crumb bar's right and the rail's bottom inset. */}
      <div className="mb-4 mr-4 flex min-h-0 flex-1 flex-col gap-[14px] overflow-y-auto lg:flex-row lg:overflow-hidden">
        {/* The whole column scrolls (scrollbar hidden), so the full AI
            suggestions and the reply box are always reachable. */}
        <div className="scrollbar-hide flex min-h-0 min-w-0 flex-1 flex-col gap-[14px] overflow-y-auto">
          <TicketCollisionBanner users={collisionUsers} />
          <TicketIdentityPlate
            ticket={ticket}
            showIdentity={false}
            requester={
              ticket.customer?.name || ticket.contactName || ticket.contactEmail
                ? {
                    name: ticket.customer?.name || ticket.contactName || ticket.contactEmail,
                    detail: ticket.customer?.email || ticket.contactEmail,
                  }
                : undefined
            }
            onOpenRequester={
              ticket.customer?.id ? () => navigate(`/customers/${ticket.customer!.id}`) : undefined
            }
            typeLabel={(ticket as any).ticketType?.name}
            resolutionDue={
              sla.resolution
                ? {
                    remaining: sla.resolution.remainingLabel,
                    at: sla.resolution.at,
                    // The band shows the countdown; 'met' and 'paused' have no
                    // running countdown to colour.
                    state:
                      sla.resolution.state === 'met' || sla.resolution.state === 'paused'
                        ? 'ok'
                        : sla.resolution.state,
                  }
                : undefined
            }
            slaDetail={sla}
            opened={deriveOpened(ticket.createdAt)}
            onChangeStatus={(s) => handleUpdateStatus(s as TicketStatus)}
            onChangePriority={(p) => handleUpdatePriority(p as TicketPriority)}
            isUpdating={isUpdating}
          />
          {/* Tabs, tab content and composer share one card. */}
          <div
            className="relative flex min-h-fit flex-1 flex-col overflow-hidden border border-[var(--co-line)] bg-[var(--co-surface)]"
            style={{ borderRadius: 14, boxShadow: 'var(--co-shadow-card)' }}
          >
          <TicketTabs
            active={activeTab}
            onChange={(k) => setActiveTab(k as typeof activeTab)}
            tabs={[
              ...(typeHasFields ? [{ key: 'lineItems', label: 'Line Items' }] : []),
              { key: 'conversation', label: 'Conversation', count: displayableConversationMessages.length },
              { key: 'notes', label: 'Notes', count: internalNotes.length },
              { key: 'history', label: 'History' },
            ]}
          />
          {/* Tab content fills the column; the composer (when the tab has one)
              is a pinned sibling below it. */}
          <div className="relative flex min-h-0 flex-1 flex-col">
            <div
              ref={scrollContainerRef}
              onScroll={onConversationScroll}
              className="scrollbar-hide min-h-[160px] flex-1 overflow-y-auto overflow-x-hidden"
            >
            {/* Swappable content */}
            {activeTab === 'lineItems' && typeHasFields && (
              <div className="p-4">
                <LineItemsTab
                  ticketId={ticket.id}
                  ticketCustomFields={ticket.customFields as Record<string, any>}
                  // A chat held in another language leaves its field values in
                  // that language, and a date the assistant resolved leaves the
                  // customer's phrasing behind. Both read under the value.
                  fieldNotes={{
                    ...(((ticket as any).metadata?.customFieldsEnglish as Record<string, string>) || {}),
                    ...(((ticket as any).metadata?.customFieldsAsGiven as Record<string, string>) || {}),
                  }}
                  ticketTypeName={(ticket as any).ticketType?.name}
                  ticketTypeId={(ticket as any).ticketType?.id ?? (ticket as any).ticketTypeId}
                />
              </div>
            )}
            {activeTab === 'conversation' && (
              <div ref={conversationContentRef}>
                <ConversationThread
                  messages={conversationMessages}
                  isLoading={isLoading}
                  isChatTicket={ticket?.channel === 'chat'}
                  ticketChannel={ticket?.channel}
                  contactEmail={ticket?.contactEmail || undefined}
                  pendingReplies={pendingReplies}
                  voiceSummary={
                    typeof (ticket?.metadata as any)?.transcriptSummary === 'string'
                      ? ((ticket?.metadata as any).transcriptSummary as string)
                      : undefined
                  }
                />
              </div>
            )}
            {activeTab === 'notes' && (
              <TicketNotesTab
                notes={internalNotes}
                onEdit={handleEditNote}
                onDelete={handleDeleteNote}
                onCreateNote={() => setNoteOpen(true)}
              />
            )}
            {activeTab === 'history' && (
              <HistoryTimeline
                entries={historyEntries}
                isLoading={historyLoading}
                hasMore={historyHasMore}
                onLoadMore={handleLoadMoreHistory}
                onRefresh={() => fetchHistory(1)}
                showFilters={true}
                emptyMessage="No history recorded for this ticket yet"
              />
            )}
            </div>

            {/* Jump-to-latest — scoped to the Conversation tab. `showLatest` is
                driven by the conversation content's bottom edge (see
                useScrollToLatest), so it appears ONLY when the newest message is
                scrolled out of view, and stays hidden once the conversation is at
                the bottom. Anchored to the thread viewport (not the column) with
                `bottom-4`, so it sits above the pinned composer. */}
            {activeTab === 'conversation' && showLatest && (
              <button
                type="button"
                onClick={() => scrollToLatest('smooth')}
                aria-label="Scroll to latest message"
                className={clsx(
                  'absolute bottom-4 right-6 z-10 inline-flex items-center gap-1.5 rounded-[2px] px-3 py-2',
                  'border border-[var(--co-brass)] bg-[var(--co-brass)] text-[var(--co-brass-on)]',
                  'font-mono text-[10.5px] font-semibold uppercase tracking-[.12em]',
                  'shadow-[0_12px_32px_rgba(35,32,28,.14)] transition-colors duration-[120ms] hover:bg-[var(--co-brass-hover)]',
                )}
              >
                <ChevronDown className="h-4 w-4" />
                <span>{newCount > 0 ? `${newCount} new` : 'Latest'}</span>
              </button>
            )}
          </div>

          {/* Reply lives ONLY here, note lives ONLY on the Notes tab. There is
              no mode toggle anywhere, which is what makes "internal note sent to
              the customer" structurally impossible rather than merely unlikely. */}
          {/* AI suggestions + reply sit at the bottom on every tab except Notes,
              which has its own note composer. */}
          {activeTab !== 'notes' && showAISuggestions && (
            <div className="flex-none border-t border-[var(--co-line)] px-6 pt-3">
              <AISuggestionsPanel
                suggestions={aiSuggestions?.suggestions || []}
                isLoading={aiLoading}
                error={aiError || undefined}
                selectedTone={selectedTone}
                onRefresh={handleRefreshSuggestions}
                onUse={handleSelectSuggestion}
                canUse={ticket.status !== 'resolved' && ticket.status !== 'closed' && !isMerged}
              />
            </div>
          )}

          {/* Replies inside their undo window — shown on every tab, sends are ticket-wide. */}
          <UndoSendBar
            items={undoSend.pending}
            now={undoSend.now}
            onUndo={undoSend.undo}
            announcement={undoSend.announcement}
          />

          {activeTab !== 'notes' && (
            <ComposerDock
              placeholder={
                requesterFirstName ? `Write a reply to ${requesterFirstName}…` : 'Write a reply…'
              }
              buttonLabel="Reply"
              buttonTone="brass"
              shortcut="R"
              hint={
                suggestionCount > 0 ? (
                  <span className="font-mono text-[9.5px] font-medium uppercase tracking-[.14em] text-[var(--co-brass-ink)]">
                    ◆ {suggestionCount} {suggestionCount === 1 ? 'suggestion' : 'suggestions'}
                  </span>
                ) : undefined
              }
              hasDraft={hasReplyDraft}
              open={replyOpen}
              onOpenChange={setReplyOpen}
            >
              <ReplyEditor
                ref={replyEditorRef}
                mode="reply"
                onCancel={() => setReplyOpen(false)}
                suggestions={
                  showAISuggestions && ticket.status !== 'resolved' && ticket.status !== 'closed' ? (
                    <ResponseSuggestions
                      suggestions={aiSuggestions?.suggestions || []}
                      isLoading={aiLoading}
                      error={aiError || undefined}
                      onSelectSuggestion={(sug) => setReplyContent(sug.content)}
                      onRefresh={handleRefreshSuggestions}
                      selectedTone={selectedTone}
                    />
                  ) : null
                }
                onSend={handleSendReply}
                isSending={isSending}
                disabled={(ticket.status === 'resolved' || ticket.status === 'closed') || isMerged}
                customerName={ticket.contactName || undefined}
                recipientName={ticket.contactName || undefined}
                recipient={ticket.contactEmail || undefined}
                // To/Cc on every ticket. Email tickets start addressed to the
                // contact; on any other channel the rows start empty and only
                // addresses the agent adds receive an email copy.
                editableRecipients
                recipientsOptional={!isEmailConversation}
                ticketNumber={String(ticket.ticketNumber ?? '')}
                placeholder={
                  isMerged
                    ? 'This ticket was merged — reply on the ticket it was merged into'
                    : (ticket.status === 'resolved' || ticket.status === 'closed')
                      ? 'Reopen ticket to reply...'
                      : 'Type your reply...'
                }
                initialContent={replyContent}
                onContentChange={handleReplyContentChange}
                onAttachFiles={handleAttachFromComposer}
                isUploadingAttachment={isUploadingAttachment}
                attachments={pendingAttachments}
                onRemoveAttachment={handleRemovePendingAttachment}
                restoreRecipients={restoreRecipients}
                onRecipientsRestored={() => setRestoreRecipients(null)}
              />
            </ComposerDock>
          )}

          {activeTab === 'notes' && (
            <ComposerDock
              placeholder="Add an internal note…"
              buttonLabel="New note"
              buttonTone="ink"
              shortcut="N"
              hint={
                <span className="font-mono text-[9.5px] font-medium uppercase tracking-[.14em] text-[var(--co-ink-4)]">
                  Not visible to customer
                </span>
              }
              hasDraft={hasNoteDraft}
              open={noteOpen}
              onOpenChange={setNoteOpen}
            >
              <ReplyEditor
                mode="note"
                onCancel={() => setNoteOpen(false)}
                onSend={handleSendNote}
                isSending={isSending}
                disabled={(ticket.status === 'resolved' || ticket.status === 'closed') || isMerged}
                ticketNumber={String(ticket.ticketNumber ?? '')}
                placeholder="Note for the team…"
                initialContent={noteContent}
                onContentChange={setNoteContent}
                onAttachFiles={handleAttachFromComposer}
                isUploadingAttachment={isUploadingAttachment}
                attachments={pendingAttachments}
                onRemoveAttachment={handleRemovePendingAttachment}
              />
            </ComposerDock>
          )}
          </div>
        </div>

        {/* Sidebar */}
        <TicketSidebar
          ticket={ticket}
          sla={sla}
          onGoToLineItems={() => setActiveTab('lineItems')}
          onUpdateStatus={handleUpdateStatus}
          onUpdatePriority={handleUpdatePriority}
          onUpdateStage={handleUpdateStage}
          onUpdatePipeline={handleUpdatePipeline}
          onAssign={handleAssign}
          onAddSecondaryAssignees={handleAddSecondaryAssignees}
          onRemoveSecondaryAssignee={handleRemoveSecondaryAssignee}
          onAddTags={handleAddTags}
          onRemoveTags={handleRemoveTags}
          onUpdateTicketType={handleUpdateTicketType}
          isUpdating={isUpdating}
          attachments={allAttachments}
          onUploadAttachments={handleUploadAttachments}
          onDeleteAttachment={requestDeleteAttachment}
          isUploadingAttachment={isUploadingAttachment}
        />
      </div>
    </div>
  );
}
